import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageSquareText, Sparkles, Activity, Users, MapPin, Compass, Layers, HandHeart, ArrowRight } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter } from "@/components/MarketingChrome";

const PILLARS = [
  { icon: MessageSquareText, title: "Resident service", body: "A concierge that answers residents by text and phone, day and night, grounded in each community's own documents, with a real person a message away when it matters." },
  { icon: Sparkles, title: "Manager effectiveness", body: "A copilot and connected tools that turn a request into a ticket, a booking or a vendor dispatch, so managers follow through instead of drowning in an inbox." },
  { icon: Activity, title: "Management intelligence", body: "Community Pulse and service patterns that show leadership where to focus next, across a single community or an entire portfolio." },
];

const VALUES = [
  { icon: Compass, title: "Grounded, not guessing", body: "Answers come from your governing documents, rules and contacts, not the open internet." },
  { icon: HandHeart, title: "Humans in the loop", body: "The AI handles the routine and hands off to a person the moment judgment is needed." },
  { icon: Layers, title: "One platform, not a bolt-on", body: "Service, work and intelligence live together, configured around each community." },
  { icon: Users, title: "Built around each community", body: "Its information, its permissions, your team's judgment, every community set up as its own." },
];

export default function AboutPage() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen" data-testid="about-page">
      <Seo
        title="About Ask Redline"
        description="Ask Redline is the AI operating platform for HOAs and community management companies, a resident concierge, a manager copilot and community intelligence, built around each community."
        canonical="/about"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> About
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">We're making communities easier to live in, and to run.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            Ask Redline is the AI operating platform for HOAs and community management companies. Residents get real answers day
            and night. Managers get a copilot that does the work. Leadership gets the intelligence to run every community, all on
            the systems you already have.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-14">
        <div className="rounded-3xl border border-sand-200 bg-white p-8 sm:p-12" data-testid="about-mission">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-redline-500">Our mission</div>
          <p className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight leading-snug max-w-4xl">
            Community management doesn't stop at the answer. Someone has to prepare the work, coordinate the next step and
            understand what needs attention. We built one platform to carry all three, so every resident is heard and every
            manager can keep up.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-6">What we do</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.title} data-testid={`about-pillar-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-3xl border border-sand-200 bg-white p-8 h-full">
                <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center mb-5"><Icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
                <h3 className="font-heading text-xl font-extrabold tracking-tight">{p.title}</h3>
                <p className="text-sm text-ink-600 mt-3 leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-6">What we believe</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={v.title} data-testid={`about-value-${i}`} className="flex items-start gap-4 rounded-2xl border border-sand-200 bg-white p-6">
                <span className="h-10 w-10 rounded-lg bg-sand-100 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-ink-700" strokeWidth={1.75} /></span>
                <div>
                  <div className="font-heading font-extrabold tracking-tight">{v.title}</div>
                  <p className="text-sm text-ink-600 mt-1.5 leading-relaxed">{v.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-10 sm:p-14">
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-redline-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="flex items-center gap-2 text-sand-300 text-sm mb-4"><MapPin className="h-4 w-4 text-redline-400" strokeWidth={1.75} /> Austin, Texas</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Come see what Redline can do for your communities.</h2>
            <p className="mt-4 text-sand-300 leading-relaxed">Book a walkthrough built around your portfolio, or, if you'd like to help build it, take a look at our open roles.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/#contact" data-testid="about-book-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 transition-colors">Book a demo <ArrowRight className="h-4 w-4" /></a>
              <Link to="/careers" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-7 py-3.5 hover:bg-white/15 transition-colors">See open roles</Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
